import { useTranslation } from 'react-i18next';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  resolveProjectScopedView,
  type CrossDivPmoDivision,
  type ProjectScopedView,
} from '@cortex/core-types';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import { useProjects } from '../../../hooks/index.js';
import { useAuth } from '../../../hooks/useAuth.js';
import { ErrorState } from '../../feedback/ErrorState/index.js';
import { ProtectedRoute } from '../../auth/index.js';
import { memberApi } from '../../../lib/api.js';
import { toGrade } from '../../../types/project.js';
import type { Project, IntegratedProduct, HealthGrade } from '../../../types/project.js';
import type { MemberOrganization } from '../../../types/api.js';
import { isActiveProject, type PageKey } from '@cortex/domain-product-innovation-lab-types';
import { HealthInfoModal, useHealthStats } from '../health/index.js';
import { ChildTicketsModal } from './ChildTicketsModal.js';
import { OnHoldProjectsView } from './OnHoldProjectsView.js';
import { ProjectTable } from './ProjectTable.js';
import { TeamView, type TeamViewMember, type TeamViewProjectRow } from './TeamView.js';
import { ProjectsLoadingSkeleton } from './ProjectsLoadingSkeleton.js';
import { ProjectsSummaryCards } from './ProjectsSummaryCards.js';
import {
  buildTeamMemberDisplayNames,
  resolveTeamMemberDisplayName,
} from '../../../lib/displayName.js';
import { CROSS_DIV_PMO_UI } from '../../../lib/cross-div-pmo-ui.js';
import { ProjectsViewHeader } from './ProjectsViewHeader.js';

// チームビュー定義（orgKey: members APIのorganizationパラメータ）
/** @graph-connects none */
const TEAM_OPTIONS = [
  { key: 'all', label: '全体', orgKey: 'all' as MemberOrganization },
  { key: 'pdg', label: 'PDG', orgKey: 'pdg' as MemberOrganization },
  { key: 'ace-ac', label: 'ACE airCloset', orgKey: 'aircloset' as MemberOrganization },
  { key: 'ace-platform', label: 'ACE Platform', orgKey: 'platform' as MemberOrganization },
  { key: 'ace-mall', label: 'ACE Mall', orgKey: 'mall' as MemberOrganization },
] as const;

type TeamKey = (typeof TEAM_OPTIONS)[number]['key'];

/** プロジェクト一覧ビューの props。 */
export interface ProjectsViewProps {
  /**
   * 表示対象を特定 Div（aC/Mall/Logi Div看板）に固定する。指定時はログインユーザー自身の所属に
   * 関係なくその Div の横断 PMO ビューを描画し、データ取得も同 Div でスコープする。未指定時は
   * ログインユーザーの所属に応じたスコープ（PI Div=全社 / 横断 PMO=自 Div）で描画する。
   */
  forcedDivision?: CrossDivPmoDivision;
  /** ルートガード（ProtectedRoute）とアクセス判定 SSOT で参照するページキー。 */
  pageKey: PageKey;
  /**
   * 初期選択するチームタブ（TEAM_OPTIONS の key）。エンジニア看板の健康度診断カードから
   * 特定チームビューへ deep-link する用途。不正値 / 未指定時は '全体'（'all'）。
   */
  initialTeam?: string;
}

/** 文字列を有効な TeamKey に解決する。不正値は 'all'。 @graph-connects none */
function resolveInitialTeam(team: string | undefined): TeamKey {
  return TEAM_OPTIONS.some((o) => o.key === team) ? (team as TeamKey) : 'all';
}

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business プロジェクト一覧ビュー。Backlog統合プロダクト・チャンク進捗テーブル、チームビュー、完了比率PieChart、保留PJ一覧、子チケットドリルダウンモーダルを表示。forcedDivision 指定で aC/Mall/Logi Div看板として特定 Div にスコープする
 * @graph-connects useProjects [calls] プロジェクト一覧取得フック呼び出し
 * @graph-connects useChildTickets [calls] 子チケット取得フック呼び出し
 * @graph-connects memberApi.getMembers [calls] チームメンバー取得APIクライアント呼び出し
 * @graph-connects ErrorState [calls] エラー表示コンポーネント描画
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects CardContent [calls] カードコンテンツコンポーネント描画
 * @graph-connects ChildTicketsModal [calls] 子チケットモーダル描画
 * @graph-connects ProjectTable [calls] プロジェクトテーブル描画
 * @graph-connects TeamView [calls] チームビュー描画
 * @graph-connects ProjectsSummaryCards [calls] サマリーカード描画
 * @graph-connects ProjectsViewHeader [calls] ヘッダー（タイトル・Div バッジ・案件アサインボタン）描画
 */
export function ProjectsView({ forcedDivision, pageKey, initialTeam }: ProjectsViewProps) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useProjects(forcedDivision);
  const { user } = useAuth();
  // forcedDivision 指定時はその Div の横断 PMO ビュー（Div 全体）に固定する。未指定時は
  // 横断スコープ：非 pi_div × pmo は Div 全体、非 pi_div × team_leader は所属チーム固定、
  // null（pi_div / admin）は従来通り全社案件。
  const projectScope: ProjectScopedView | null = forcedDivision
    ? { division: forcedDivision, lockedTeam: null }
    : resolveProjectScopedView(user?.division, user?.portalRoles, user?.teamName);
  const crossDivPmoUi = projectScope ? CROSS_DIV_PMO_UI[projectScope.division] : null;
  // team_leader 用にチーム filter を固定するための値（PMO や PI Div は null = 通常通り選択可能）。
  const lockedTeam = projectScope?.lockedTeam ?? null;
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<TeamKey>(() => resolveInitialTeam(initialTeam));
  const [showOnHold, setShowOnHold] = useState(false);
  // チームリーダー時はマウント時に lockedTeam を初期値として注入する。useState の lazy initializer
  // 経由で /auth/me 解決後の初回レンダリングから固定値を保つ。
  // ただし useAuth が初回 mount 時に未解決（user=null）だった場合、lockedTeam も null となり
  // filterTeam は空文字で初期化される。後の useAuth 解決で lockedTeam が確定したら下の
  // useEffect で filterTeam を同期する。
  const [filterTeam, setFilterTeam] = useState(lockedTeam ?? '');

  // lockedTeam が後から解決された場合（useAuth が mount 後に user を返した場合）に
  // filterTeam を lockedTeam に同期する。これがないと UI 上 "すべて" のままで data 上は
  // scope が効いている、という不整合が出る。
  useEffect(() => {
    if (lockedTeam && filterTeam !== lockedTeam) {
      setFilterTeam(lockedTeam);
    }
  }, [lockedTeam, filterTeam]);
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterProgress, setFilterProgress] = useState('');
  const [filterDevLeader, setFilterDevLeader] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterHealth, setFilterHealth] = useState('');
  const [showHealthInfo, setShowHealthInfo] = useState(false);
  // 横断 PMO の business 切替（aC Div のみ）。null は「全 business」を意味する。
  const [businessFilter, setBusinessFilter] = useState<string | null>(null);

  // チームメンバー取得（チームタブ選択時）
  const selectedOrgKey = TEAM_OPTIONS.find((o) => o.key === selectedTeam)?.orgKey;
  const { data: membersData } = useQuery({
    queryKey: ['team-members', selectedOrgKey],
    queryFn: () => memberApi.getMembers({ organization: selectedOrgKey }),
    enabled: selectedTeam !== 'all',
    staleTime: 10 * 60 * 1000,
  });

  // チームメンバーのemailセット
  const teamMemberEmails = useMemo(() => {
    if (!membersData) return null;
    const emails = new Set<string>();
    for (const m of [...membersData.engineers, ...membersData.qa]) {
      emails.add(m.email.toLowerCase());
    }
    return emails;
  }, [membersData]);

  // email → 表示名（ニックネーム優先、未設定時は本名）の解決マップ
  const teamMemberDisplayNames = useMemo(() => {
    if (!membersData) return null;
    return buildTeamMemberDisplayNames([...membersData.engineers, ...membersData.qa]);
  }, [membersData]);

  // プロジェクトID → 担当メンバー一覧（子チケットアサイニーベース、チームビューグルーピング用）。
  // 子チケットのアサイニーが 1 人もいない母票は BE 側で開発リーダーが補完され、isDevLeader = true で届く。
  const projectMembers = useMemo(() => {
    if (!data) return null;
    const map = new Map<
      number,
      { name: string; email: string; role: TeamViewProjectRow['role'] }[]
    >();
    for (const a of data.memberAssignments) {
      const list = map.get(a.projectId) ?? [];
      list.push({
        name: a.memberName,
        email: a.memberEmail,
        role: { isDeveloper: a.isDeveloper, isReviewer: a.isReviewer, isDevLeader: a.isDevLeader },
      });
      map.set(a.projectId, list);
    }
    return map;
  }, [data]);

  // フィルタ用の選択肢を収集
  const filterOptions = useMemo(() => {
    if (!data) return { teams: [], assignees: [], devLeaders: [], statuses: [] };
    const allProjects = [
      ...data.integratedProducts.flatMap((ip) => ip.chunkProjects),
      ...data.standaloneProjects,
    ];
    // team_leader は自チームのみ。横断 PMO は UI SSOT のホワイトリスト固定。PI Div ビューは
    // 従来通りデータから動的収集する。
    const teams = lockedTeam
      ? [lockedTeam]
      : crossDivPmoUi
        ? [...crossDivPmoUi.teams]
        : [
            ...new Set(
              allProjects.map((p) => p.responsibleTeam).filter((v): v is string => v !== null),
            ),
          ].sort();
    return {
      teams,
      assignees: [
        ...new Set(allProjects.map((p) => p.assignee).filter((v): v is string => v !== null)),
      ].sort(),
      devLeaders: [
        ...new Map(
          allProjects
            .map((p) => p.devLeader)
            .filter((v): v is string => v !== null)
            .map((v) => [v.toLowerCase(), v]),
        ).values(),
      ].sort(),
      statuses: [...new Set(allProjects.map((p) => p.status).filter(Boolean))].sort(),
    };
  }, [data, crossDivPmoUi, lockedTeam]);

  // team_leader はチームを変更できないようにラップする（filterTeam の解除や別チーム選択を防ぐ）。
  const setFilterTeamGuarded = useCallback(
    (next: string) => {
      if (lockedTeam) return;
      setFilterTeam(next);
    },
    [lockedTeam],
  );

  // チームメンバーマッチング（開発系子チケットのアサイニーで判定）
  const matchesTeamMembers = useCallback(
    (p: Project): boolean => {
      if (selectedTeam === 'all') return true;
      if (!teamMemberEmails || !projectMembers) return false;
      const members = projectMembers.get(p.id);
      if (!members) return false;
      return members.some((m) => teamMemberEmails.has(m.email.toLowerCase()));
    },
    [selectedTeam, teamMemberEmails, projectMembers],
  );

  // プルダウンフィルタ適用
  const filterProject = useCallback(
    (p: Project): boolean => {
      if (!matchesTeamMembers(p)) return false;
      // 横断 PMO: business 切替タブが選択されている場合は client-side で business 一致のみ通す。
      if (businessFilter && p.business !== businessFilter) return false;
      if (filterTeam && p.responsibleTeam !== filterTeam) return false;
      if (filterAssignee && p.assignee !== filterAssignee) return false;
      if (filterDevLeader && p.devLeader?.toLowerCase() !== filterDevLeader.toLowerCase())
        return false;
      if (filterStatus && p.status !== filterStatus) return false;
      if (filterProgress) {
        const r = p.completionRatio ?? 0;
        if (filterProgress === 'low' && r >= 40) return false;
        if (filterProgress === 'mid' && (r < 40 || r >= 90)) return false;
        if (filterProgress === 'high' && r < 90) return false;
      }
      if (filterHealth) {
        const g = p.health?.grade;
        if (filterHealth === 'healthy' && g !== 'S' && g !== 'A') return false;
        if (filterHealth === 'warning' && g !== 'B') return false;
        if (filterHealth === 'danger' && g !== 'C' && g !== 'D') return false;
        if (filterHealth === 'none' && p.health !== null) return false;
      }
      return true;
    },
    [
      matchesTeamMembers,
      businessFilter,
      filterTeam,
      filterAssignee,
      filterProgress,
      filterDevLeader,
      filterStatus,
      filterHealth,
    ],
  );

  const filtered = useMemo(() => {
    if (!data) return null;

    const filteredIntegrated = data.integratedProducts
      .map((ip) => {
        const filteredChunks = ip.chunkProjects.filter(filterProject);
        if (filteredChunks.length > 0) {
          return { ...ip, chunkProjects: filteredChunks };
        }
        return null;
      })
      .filter((ip): ip is IntegratedProduct => ip !== null);

    const filteredStandalone = data.standaloneProjects.filter(filterProject);

    return { integratedProducts: filteredIntegrated, standaloneProjects: filteredStandalone };
  }, [data, filterProject]);

  // チームビュー: メンバー別案件一覧（開発系子チケットのアサイニーでグルーピング）。
  // 進行中一覧から外れる「未対応・PL未アサイン」母票もここでは表示する（担当者から案件が消えないため）。
  // 進行中PJ数 / 健康度分布 / 進行中一覧テーブルの口径は変えない（pendingUnassignedProjects は別枠）。
  const teamView = useMemo((): TeamViewMember[] | null => {
    if (!data || selectedTeam === 'all' || !teamMemberEmails || !projectMembers) return null;

    // 統合プロダクト配下チャンクには未対応・未アサイン母票が既に含まれうるため id で重複排除する
    const uniqueById = new Map<number, Project>();
    for (const p of [
      ...data.integratedProducts.flatMap((ip) => ip.chunkProjects),
      ...data.standaloneProjects,
      ...(data.pendingUnassignedProjects ?? []),
    ]) {
      if (!uniqueById.has(p.id)) uniqueById.set(p.id, p);
    }
    const allProjects = [...uniqueById.values()]
      .filter(filterProject)
      .filter((p) => p.status !== '完了');

    // メンバーごとにグルーピング（チームメンバーのみ、同一プロジェクト重複なし）
    const byMember = new Map<string, { displayName: string; rows: TeamViewProjectRow[] }>();
    for (const p of allProjects) {
      const members = projectMembers.get(p.id);
      if (!members) continue;
      for (const m of members) {
        const emailKey = m.email.toLowerCase();
        if (!teamMemberEmails.has(emailKey)) continue;
        const entry = byMember.get(emailKey);
        if (!entry) {
          const displayName = resolveTeamMemberDisplayName(teamMemberDisplayNames, m);
          byMember.set(emailKey, { displayName, rows: [{ project: p, role: m.role }] });
          continue;
        }
        // 同一プロジェクトの重複を防止
        if (!entry.rows.some((r) => r.project.id === p.id)) {
          entry.rows.push({ project: p, role: m.role });
        }
      }
    }

    // 名前順にソート、各メンバーの案件は進捗率降順
    return [...byMember.values()]
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map(
        ({ displayName, rows }) =>
          [
            displayName,
            [...rows].sort(
              (a, b) => (b.project.completionRatio ?? 0) - (a.project.completionRatio ?? 0),
            ),
          ] as TeamViewMember,
      );
  }, [data, selectedTeam, filterProject, teamMemberEmails, teamMemberDisplayNames, projectMembers]);

  // 健康度分布の集計はテーブルと同じフィルタ条件を反映する（チーム / 主担当 / 開発リーダー /
  // ステータス / 進捗率 / business / PDG-ACE タブ切替）。filterHealth 自体は健康度別の
  // サブセットを返すフィルタなので、それでパイチャートを絞ると円グラフが自己循環するため除外する。
  const allProjectsForHealth = useMemo(() => {
    if (!data) return [];
    return [
      ...data.integratedProducts.flatMap((ip) => ip.chunkProjects),
      ...data.standaloneProjects,
    ].filter((p) => {
      // 横断 PMO スコープで一覧入りする完了母票は健康度を持たないため、健康度パイは進行中のみで集計する
      // （ヘッダーの健康度分布の意味を維持）。PI Div 通常ビューは元々進行中のみなので影響しない。
      if (!isActiveProject(p)) return false;
      if (!matchesTeamMembers(p)) return false;
      if (businessFilter && p.business !== businessFilter) return false;
      if (filterTeam && p.responsibleTeam !== filterTeam) return false;
      if (filterAssignee && p.assignee !== filterAssignee) return false;
      if (filterDevLeader && p.devLeader?.toLowerCase() !== filterDevLeader.toLowerCase())
        return false;
      if (filterStatus && p.status !== filterStatus) return false;
      if (filterProgress) {
        const r = p.completionRatio ?? 0;
        if (filterProgress === 'low' && r >= 40) return false;
        if (filterProgress === 'mid' && (r < 40 || r >= 90)) return false;
        if (filterProgress === 'high' && r < 90) return false;
      }
      return true;
    });
  }, [
    data,
    matchesTeamMembers,
    businessFilter,
    filterTeam,
    filterAssignee,
    filterDevLeader,
    filterStatus,
    filterProgress,
  ]);

  // 横断 PMO 用の進行中 / 保留中 PJ 数（business 切替反映）。
  // PI Div ビューでは従来通り data.summary.activeProjectCount を表示する。
  // 横断 PMO スコープでは完了母票も一覧表示するため、進行中PJ数は進行中ステータスのみを数える
  // （ヘッダーの「進行中PJ数」の意味を維持）。
  const scopedActiveCount = useMemo(() => {
    if (!filtered) return null;
    const activeIntegrated = filtered.integratedProducts.filter(
      (ip) => ip.status !== '完了' && ip.chunkProjects.some(isActiveProject),
    ).length;
    const activeStandalone = filtered.standaloneProjects.filter(isActiveProject).length;
    return activeIntegrated + activeStandalone;
  }, [filtered]);
  const scopedOnHoldCount = useMemo(() => {
    if (!data) return 0;
    if (!businessFilter) return data.onHoldProjects.length;
    return data.onHoldProjects.filter((p) => p.business === businessFilter).length;
  }, [data, businessFilter]);
  // 横断 PMO 用の「今月中実績」: 完了済み Gen2（テーブル非表示）も含めた scopedMonthlyActualProjects
  // を対象に、現在の filter（team / 主担当 / 開発リーダー / status / 進捗率 / business）を適用して
  // 合算する。filterHealth は完了 Gen2 に health がないため適用しない（健康度パイと同じ方針）。
  // 1ポイント未満を切り捨てて整数表示するため Math.round で丸める。
  const scopedMonthlyActualPoint = useMemo(() => {
    if (!data?.scopedMonthlyActualProjects) return 0;
    const total = data.scopedMonthlyActualProjects
      .filter((p) => {
        if (!matchesTeamMembers(p)) return false;
        if (businessFilter && p.business !== businessFilter) return false;
        if (filterTeam && p.responsibleTeam !== filterTeam) return false;
        if (filterAssignee && p.assignee !== filterAssignee) return false;
        if (filterDevLeader && p.devLeader?.toLowerCase() !== filterDevLeader.toLowerCase())
          return false;
        if (filterStatus && p.status !== filterStatus) return false;
        if (filterProgress) {
          const r = p.completionRatio ?? 0;
          if (filterProgress === 'low' && r >= 40) return false;
          if (filterProgress === 'mid' && (r < 40 || r >= 90)) return false;
          if (filterProgress === 'high' && r < 90) return false;
        }
        return true;
      })
      .reduce((acc, p) => acc + (p.monthlyActualPoint ?? 0), 0);
    return Math.round(total);
  }, [
    data?.scopedMonthlyActualProjects,
    matchesTeamMembers,
    businessFilter,
    filterTeam,
    filterAssignee,
    filterDevLeader,
    filterStatus,
    filterProgress,
  ]);

  const healthStats = useHealthStats(allProjectsForHealth);

  const healthGradeFromAvg: HealthGrade = toGrade(healthStats.avgScore);

  // 保留中の表示（独立サブ画面。子チケット/健康度モーダルの state は OnHoldProjectsView 側で持つ）
  if (showOnHold && data) {
    return (
      <OnHoldProjectsView
        projects={data.onHoldProjects}
        onBack={() => {
          setShowOnHold(false);
        }}
      />
    );
  }

  return (
    <ProtectedRoute pageKey={pageKey}>
      <div className="space-y-6 animate-fade-in">
        <ProjectsViewHeader crossDivPmoUi={crossDivPmoUi} />

        {error && (
          <ErrorState
            title={t('feedback.errorTitle')}
            message={t('feedback.errorMessage')}
            onRetry={() => {
              void refetch();
            }}
          />
        )}

        {isLoading ? (
          <ProjectsLoadingSkeleton />
        ) : data ? (
          <>
            <ProjectsSummaryCards
              activeProjectCount={
                crossDivPmoUi ? (scopedActiveCount ?? 0) : data.summary.activeProjectCount
              }
              onHoldCount={crossDivPmoUi ? scopedOnHoldCount : data.onHoldProjects.length}
              crossDivPmoUi={crossDivPmoUi}
              monthlyActualPoint={scopedMonthlyActualPoint}
              healthStats={healthStats}
              healthGrade={healthGradeFromAvg}
              onShowOnHold={() => {
                setShowOnHold(true);
              }}
            />

            {/* 進行中PJ一覧 */}
            <Card className="animate-content-reveal">
              <div className="flex items-center justify-between px-6 pt-5 pb-0">
                <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
                  {t('projects.activeProjects')}
                </h3>
                {/* 横断 PMO 以外（PI Div）はチームビュータブ */}
                {!crossDivPmoUi && (
                  <TabGroup ariaLabel={t('projects.teamSwitchLabel')}>
                    {TEAM_OPTIONS.map((opt) => (
                      <TabButton
                        key={opt.key}
                        active={selectedTeam === opt.key}
                        label={opt.label}
                        onClick={() => {
                          setSelectedTeam(opt.key);
                        }}
                      />
                    ))}
                  </TabGroup>
                )}
              </div>

              {/* 横断 PMO の business 切替（aC Div のみ。Mall / Log は business 1 種のため非表示） */}
              {crossDivPmoUi?.businessTabs && (
                <div className="px-6 pt-3 pb-0">
                  <TabGroup ariaLabel={t('projects.businessSwitchLabel')}>
                    <TabButton
                      active={businessFilter === null}
                      label={t('projects.businessAll')}
                      onClick={() => {
                        setBusinessFilter(null);
                      }}
                    />
                    {crossDivPmoUi.businessTabs.map((b) => (
                      <TabButton
                        key={b}
                        active={businessFilter === b}
                        label={b}
                        onClick={() => {
                          setBusinessFilter(b);
                        }}
                      />
                    ))}
                  </TabGroup>
                </div>
              )}

              <CardContent>
                {/* チームビュー: PI Div ビューでのみ有効。横断 PMO は常に通常テーブル。 */}
                {!crossDivPmoUi && selectedTeam !== 'all' && teamView ? (
                  <TeamView
                    teamView={teamView}
                    onProjectClick={setSelectedProject}
                    onHealthInfoClick={() => {
                      setShowHealthInfo(true);
                    }}
                  />
                ) : filtered ? (
                  <ProjectTable
                    integratedProducts={filtered.integratedProducts}
                    standaloneProjects={filtered.standaloneProjects}
                    onProjectClick={setSelectedProject}
                    onHealthInfoClick={() => {
                      setShowHealthInfo(true);
                    }}
                    emptyMessage={t('projects.noProjects')}
                    filters={{
                      filterTeam,
                      // team_leader はチームを変更できない。ガード付きセッターを渡す。
                      setFilterTeam: setFilterTeamGuarded,
                      filterAssignee,
                      setFilterAssignee,
                      filterProgress,
                      setFilterProgress,
                      filterDevLeader,
                      setFilterDevLeader,
                      filterStatus,
                      setFilterStatus,
                      filterHealth,
                      setFilterHealth,
                      options: filterOptions,
                      getTeamOptionColor: crossDivPmoUi
                        ? (opt: string) => crossDivPmoUi.teamColors[opt]
                        : undefined,
                      teamFilterLocked: lockedTeam !== null,
                    }}
                  />
                ) : null}
              </CardContent>
            </Card>
          </>
        ) : null}

        {/* 子チケット詳細モーダル */}
        <ChildTicketsModal
          open={selectedProject !== null}
          onClose={() => {
            setSelectedProject(null);
          }}
          project={selectedProject}
        />
        <HealthInfoModal
          open={showHealthInfo}
          onClose={() => {
            setShowHealthInfo(false);
          }}
        />
      </div>
    </ProtectedRoute>
  );
}
